import React, { useState, useEffect, useCallback, useMemo, lazy, Suspense } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { notify } from '@/utils/notification';
import { useTranslation } from 'react-i18next';

import {
  SidebarProvider,
  Sidebar,
  SidebarContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarHeader,
  SidebarTrigger,
  SidebarInset,
  SidebarFooter,
  useSidebar
} from '@/components/ui/sidebar';

import { LogOut } from 'lucide-react';
import { Home, User, Calendar, FileText, CreditCard, Settings, Sparkles, BarChart3, CalendarDays } from 'lucide-react';

import ClientProfile from '@/components/dashboard/client/ClientProfile';
import UpcomingAppointments from '@/components/dashboard/client/UpcomingAppointments';
import AppointmentHistory from '@/components/dashboard/client/AppointmentHistory';

// Lazy load heavy components for better performance
const PaymentMethods = lazy(() => import('@/components/dashboard/client/PaymentMethods'));
const ClientSettings = lazy(() => import('@/components/dashboard/client/ClientSettings'));
const TrackingAnalysis = lazy(() => import('@/components/dashboard/client/tracking/TrackingAnalysis'));
const CalendarView = lazy(() => import('@/components/calendar/CalendarView'));
import Navbar from '@/components/Navbar';
import { useIsMobile } from '@/hooks/use-mobile';
import { SidebarMenuButtonWithAutoHide } from '@/components/dashboard/shared/SidebarMenuButtonWithAutoHide';
import { UnifiedLoader } from '@/components/dashboard/shared/UnifiedLoader';
import { UnifiedError } from '@/components/dashboard/shared/UnifiedError';
import { useUnreadMessages } from '@/hooks/useUnreadMessages';
import SystemMessagesList from '@/components/dashboard/shared/SystemMessagesList';
import { getFullName } from '@/utils/name-helpers';
import { TimezoneDetectionAlert } from '@/components/dashboard/shared/TimezoneDetectionAlert';

const ClientDashboard = () => {
  const { user, isLoading: authLoading, activeRole, signOut, roleReady } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const { t } = useTranslation('dashboard');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [clientProfile, setClientProfile] = useState(null);
  const [profileMissing, setProfileMissing] = useState(false);
  const isMobile = useIsMobile();
  
  // Unread messages hook
  const { unreadCounts } = useUnreadMessages({ 
    userId: undefined, // Will be set by the hook from auth context
    userType: 'client'
  });
  
  // Debug logging
  console.log('📱 ClientDashboard - unreadCounts:', unreadCounts);
  
  // Parse query parameters to get initial tab
  const queryParams = new URLSearchParams(location.search);
  const initialTab = queryParams.get('tab') || 'home';
  const [activeTab, setActiveTab] = useState(initialTab);

  const handleSignOut = useCallback(async () => {
    try {
      await signOut();
      navigate('/');
      notify.success("Logged Out", "You've been successfully logged out");
    } catch (error) {
      console.error('Sign out error:', error);
      notify.error("Error", "Failed to sign out. Please try again.");
    }
  }, [signOut, navigate]);

  useEffect(() => {
    const checkAuth = async () => {
      if (authLoading || !roleReady) return;

      setError(null);
      
      try {
        if (!user) {
          notify.error("Unauthorized", "Please login to view your dashboard");
          navigate("/client-auth");
          setIsLoading(false);
          return;
        }

        const { data, error } = await supabase
          .from('clients')
          .select('*')
          .eq('id', user.id)
          .maybeSingle();
        
        if (error) {
          console.error("Error fetching client profile:", error);
          setError("Could not load your client profile");
          setIsLoading(false);
          return;
        }

        if (!data) {
          console.log("No client profile found. Showing inline guidance instead of creating.");
          setProfileMissing(true);
          setIsLoading(false);
          return;
        }
        
        setClientProfile(data);
        setIsLoading(false);
      } catch (err) {
        console.error("Unexpected error:", err);
        setError("An unexpected error occurred while loading your profile");
        setIsLoading(false);
      }
    };
    
    checkAuth();
  }, [user, authLoading, roleReady, navigate]);

  // Update URL when tab changes
  useEffect(() => {
    const newParams = new URLSearchParams(location.search);
    newParams.set('tab', activeTab);
    navigate(`${location.pathname}?${newParams.toString()}`, { replace: true });
  }, [activeTab, navigate, location.pathname, location.search]);

  if (authLoading || !roleReady || isLoading) {
    return <UnifiedLoader message={t('loading')} fullScreen />;
  }

  if (error) {
    return (
      <UnifiedError 
        message={error} 
        onRetry={() => window.location.reload()} 
        fullScreen
      />
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      {/* Timezone detection - runs once per session */}
      {user && clientProfile && (
        <TimezoneDetectionAlert
          userId={user.id}
          userType="client"
          currentTimezone={clientProfile.timezone || 'America/New_York'}
        />
      )}
      <SidebarProvider>
        <div className="min-h-screen flex w-full pt-16">
          <Sidebar variant="sidebar" collapsible="icon" className="border-r border-gray-100 transition-all duration-300 ease-in-out">
            <SidebarHeader className="border-b px-4 py-3">
              <div className="text-lg font-bold text-amber-600">{t('dashboard')}</div>
            </SidebarHeader>
            <SidebarContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButtonWithAutoHide 
                    tooltip={t('home')} 
                    onClick={() => setActiveTab('home')}
                    isActive={activeTab === 'home'}
                    countRefreshTrigger={0}
                  >
                    <Home className="mr-2" />
                    <span>{t('home')}</span>
                  </SidebarMenuButtonWithAutoHide>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButtonWithAutoHide 
                    tooltip={t('myProfile')} 
                    onClick={() => setActiveTab('profile')}
                    isActive={activeTab === 'profile'}
                    countRefreshTrigger={0}
                  >
                    <User className="mr-2" />
                    <span>{t('myProfile')}</span>
                  </SidebarMenuButtonWithAutoHide>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButtonWithAutoHide 
                    tooltip={t('calendar.calendarView')} 
                    onClick={() => setActiveTab('calendar')}
                    isActive={activeTab === 'calendar'}
                    countRefreshTrigger={0}
                  >
                    <CalendarDays className="mr-2" />
                    <span>{t('calendar.calendar')}</span>
                  </SidebarMenuButtonWithAutoHide>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButtonWithAutoHide 
                    tooltip="Upcoming" 
                    onClick={() => setActiveTab('upcoming')}
                    isActive={activeTab === 'upcoming'}
                    showBadge={true}
                    unreadCount={unreadCounts.totalUnread}
                    countRefreshTrigger={0}
                  >
                    <Calendar className="mr-2" />
                    <span>Upcoming</span>
                  </SidebarMenuButtonWithAutoHide>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButtonWithAutoHide 
                    tooltip="History" 
                    onClick={() => setActiveTab('history')}
                    isActive={activeTab === 'history'}
                    showBadge={true}
                    unreadCount={unreadCounts.totalUnread}
                    countRefreshTrigger={0}
                  >
                    <FileText className="mr-2" />
                    <span>History</span>
                  </SidebarMenuButtonWithAutoHide>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButtonWithAutoHide 
                    tooltip={t('paymentMethods')} 
                    onClick={() => setActiveTab('payment')}
                    isActive={activeTab === 'payment'}
                    countRefreshTrigger={0}
                  >
                    <CreditCard className="mr-2" />
                    <span>{t('paymentMethods')}</span>
                  </SidebarMenuButtonWithAutoHide>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButtonWithAutoHide 
                    tooltip={t('tracking')} 
                    onClick={() => setActiveTab('tracking')}
                    isActive={activeTab === 'tracking'}
                    countRefreshTrigger={0}
                  >
                    <BarChart3 className="mr-2" />
                    <span>{t('tracking')}</span>
                  </SidebarMenuButtonWithAutoHide>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButtonWithAutoHide 
                    tooltip={t('settings')} 
                    onClick={() => setActiveTab('settings')}
                    isActive={activeTab === 'settings'}
                    countRefreshTrigger={0}
                  >
                    <Settings className="mr-2" />
                    <span>{t('settings')}</span>
                  </SidebarMenuButtonWithAutoHide>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarContent>
            <ClientFooter onSignOut={handleSignOut} userEmail={user?.email} />
          </Sidebar>
          <SidebarInset className="bg-gray-50 flex-1 transition-all duration-300 ease-in-out">
            <header className="sticky top-0 z-40 bg-white border-b h-16 flex items-center px-4">
              <SidebarTrigger className="mr-4" />
            </header>
              <div className="w-full p-6">
                <div className="bg-white rounded-lg p-6 shadow-sm w-full">
                  {profileMissing && (
                    <div className="mb-6 rounded-md border border-amber-200 bg-amber-50 p-4 text-amber-900">
                      <p className="font-medium">Client profile not found.</p>
                      <p className="text-sm mt-1">Please complete your profile in Settings to unlock all features.</p>
                    </div>
                  )}
                  {
                  activeTab === 'home' && (
                    <div className="min-h-[400px] space-y-6">
                      <div>
                        <h2 className="text-2xl font-medium mb-4">{t('welcomeToDashboard')}</h2>
                        <p className="text-gray-600 mb-6">
                          {t('hello')} {getFullName(clientProfile) || user?.email?.split('@')[0] || 'there'}! {t('personalDashboard')}
                        </p>
                        <p className="text-gray-600 mb-6">
                          {t('navigateToManage')}
                        </p>
                      </div>
                      
                      <SystemMessagesList userType="client" />
                    </div>
                  )
                  }
                  
                  {activeTab === 'profile' && (
                    <ClientProfile profile={clientProfile} setProfile={setClientProfile} />
                  )}
                  
                  {activeTab === 'upcoming' && (
                    <UpcomingAppointments unreadByBooking={unreadCounts.unreadByBooking} />
                  )}
                  
                  {activeTab === 'history' && (
                    <AppointmentHistory unreadByBooking={unreadCounts.unreadByBooking} />
                  )}
                  
                  {activeTab === 'payment' && (
                    <Suspense fallback={<UnifiedLoader message="Loading payment methods..." />}>
                      <PaymentMethods />
                    </Suspense>
                  )}
                  
                  {activeTab === 'tracking' && (
                    <Suspense fallback={<UnifiedLoader message="Loading tracking analysis..." />}>
                      <TrackingAnalysis />
                    </Suspense>
                  )}
                  
                  {activeTab === 'calendar' && (
                    <Suspense fallback={<UnifiedLoader message="Loading calendar..." />}>
                      <CalendarView />
                    </Suspense>
                  )}
                  
                  {activeTab === 'settings' && (
                    <Suspense fallback={<UnifiedLoader message="Loading settings..." />}>
                      <ClientSettings clientId={user?.id} profile={clientProfile} setProfile={setClientProfile} />
                    </Suspense>
                  )}
                </div>
              </div>
          </SidebarInset>
        </div>
      </SidebarProvider>
    </div>
  );
};

// Simplified footer component with stable layout
function ClientFooter({ onSignOut, userEmail }: { onSignOut: () => void; userEmail?: string }) {
  const { expanded } = useSidebar();
  const isMobile = useIsMobile();
  const { t } = useTranslation('dashboard');

  return (
    <SidebarFooter className="border-t mt-auto p-4">
      <SidebarMenuItem>
        <SidebarMenuButton 
          tooltip={t('logout')} 
          onClick={onSignOut}
          className="text-gray-600 hover:text-red-600 justify-center"
        >
          <LogOut className={expanded || isMobile ? "mr-2" : ""} />
          {(expanded || isMobile) && <span>{t('logout')}</span>}
        </SidebarMenuButton>
      </SidebarMenuItem>
      {userEmail && (
        <div className={`mt-2 text-xs text-gray-500 px-2 transition-opacity duration-200 ${
          expanded || isMobile ? 'opacity-100' : 'opacity-0'
        }`}>
          {(expanded || isMobile) && (
            <span>{t('loggedInAs')} {userEmail}</span>
          )}
        </div>
      )}
    </SidebarFooter>
  );
}

export default ClientDashboard;
